import { useContext, useEffect, useState } from 'react';

import { FirstLoadGateSuppressionContext } from '@revelio/core/first-load-gate';

import { ContentGrid } from '../ContentGrid/ContentGrid';
import { DataResolver } from '../DataResolver/DataResolver';
import {
  TabContentProviderForTab,
  usePageConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { usePageState } from '../PageState/PageStateProvider';
import { resolveActiveTab } from '../PageState/resolveActiveTab';
import { TabVisibilityProvider } from '../TabVisibilityContext';

/**
 * Renders the content grid for the active tab while keeping every previously
 * visited tab mounted but hidden, so switching back to a tab is instant instead
 * of rebuilding its entire chart grid from scratch.
 *
 * A tab is mounted lazily on its first visit (unvisited tabs cost nothing until
 * opened) and then kept alive. Hidden tabs are `display:none` and their queries
 * pause — via `TabVisibilityProvider` → `useUrqlQueryWithStatus` — so they don't
 * refetch in the background; showing one again reads straight from the cache.
 *
 * Single-tab pages render exactly one always-visible grid, so their behaviour is
 * unchanged.
 */
export const KeepAliveTabGrids = () => {
  const { config } = usePageConfig();
  const { pageState } = usePageState();
  // ORed per tab below rather than overwritten: a host that has already
  // switched its loaders off (the assistant's offscreen previews) must stay
  // off for the visible tab too.
  const isLoaderCountingSuppressed = useContext(
    FirstLoadGateSuppressionContext
  );
  const tabs = config.tabs;
  const activeTabId = resolveActiveTab(tabs, pageState.activeTabId)?.id;

  const [visitedTabIds, setVisitedTabIds] = useState<string[]>(() =>
    activeTabId ? [activeTabId] : []
  );

  useEffect(() => {
    if (activeTabId && !visitedTabIds.includes(activeTabId)) {
      setVisitedTabIds((prev) =>
        prev.includes(activeTabId) ? prev : [...prev, activeTabId]
      );
    }
  }, [activeTabId, visitedTabIds]);

  // Include the active tab immediately on its first visit — before the effect
  // above commits the visited set — so a switch never flashes an empty frame.
  const mountedTabIds =
    activeTabId && !visitedTabIds.includes(activeTabId)
      ? [...visitedTabIds, activeTabId]
      : visitedTabIds;

  return (
    <>
      {tabs
        .filter((tab) => mountedTabIds.includes(tab.id))
        .map((tab) => {
          const isVisible = tab.id === activeTabId;
          return (
            <TabContentProviderForTab key={tab.id} tab={tab}>
              <TabVisibilityProvider isVisible={isVisible}>
                <DataResolver items={tab.tabContent.items}>
                  {/* A hidden tab's paused charts sit on their loader
                      indefinitely. Left counted, a page would never read as
                      rendered in the Data Inspector after a tab switch. */}
                  <FirstLoadGateSuppressionContext.Provider
                    value={isLoaderCountingSuppressed || !isVisible}
                  >
                    <ContentGrid hidden={!isVisible} />
                  </FirstLoadGateSuppressionContext.Provider>
                </DataResolver>
              </TabVisibilityProvider>
            </TabContentProviderForTab>
          );
        })}
    </>
  );
};
